import {Circle, GeoJSON, LayerGroup, Polyline} from 'react-leaflet'
import * as React from 'react'
import {dataforvalterMapConfig} from "../../../map/map-config";

import {GeometryObject} from "geojson";
import {RefObject} from "react";
import classNames = require("classnames");
import * as WKTParser from "terraformer-wkt-parser";
import MapUtils from "../../../map/map-utils";
import {LocationalRoadObject} from "../LocationalState";

interface LayerProps {
    roadObject: LocationalRoadObject,
    geometry: GeometryObject,
    isSelected: boolean,
    roadObjectClicked: Function,
    confidence: string,
}

export default class LocationalMapLayer extends React.Component<LayerProps> {

    private readonly ref: RefObject<LayerGroup>;

    constructor(props: LayerProps) {
        super(props);
        this.ref = React.createRef();
    }

    componentDidUpdate(prevProps: Readonly<LayerProps>) {
        this.bringLayersToFront();

        if (this.props.isSelected !== prevProps.isSelected) {
            this.updateSelectedClasses(this.ref?.current?.leafletElement?.getLayers() ?? []);
        }

        if (this.props.confidence !== prevProps.confidence){
            this.updateConfidenceClasses(this.ref?.current?.leafletElement?.getLayers() ?? [], prevProps.confidence);
        }
    }

    bringLayersToFront() {
        const {isSelected} = this.props;
        if (isSelected) {
            let layers = this.ref?.current?.leafletElement?.getLayers() ?? [];
            layers.forEach(layer => (layer as any).bringToFront ? (layer as any).bringToFront() : null );
        }
    }

    updateSelectedClasses(layers: any[]){
        const {isSelected} = this.props;

        layers.forEach(layer => {
            let path = layer._path;
            if (path) {
                if (isSelected) {
                    path.classList.add("selected");
                } else {
                    path.classList.remove("selected");
                }
            }
            if (layer.getLayers) {
                this.updateSelectedClasses(layer.getLayers())
            }
        })
    }

    updateConfidenceClasses(layers: any[], prevConfidence: string){
        const {confidence} = this.props;
        layers.forEach(layer => {
            let path = layer._path;
            if (path) {
                if (path.classList.contains(prevConfidence)) {
                    path.classList.remove(prevConfidence);
                    path.classList.add(confidence);
                }
            }
            if (layer.getLayers) {
                this.updateConfidenceClasses(layer.getLayers(), prevConfidence);
            }
        })
    }


    render(): React.ReactNode {
        let {} = this.props;

        return (
            <LayerGroup ref={this.ref}>
                {this.generateOutlineFeature()}
                {this.generateFeature()}
                {this.generateLocationalLayers()}
            </LayerGroup>
        )
    }

    generateLocationalLayers(){
        let {geometry, roadObject, isSelected} = this.props;
        let locationalLayers = [];
        if (roadObject.locational?.wkt) {
            const locationalPosUtm33 = WKTParser.parse(roadObject.locational.wkt);
            const locationalPosition: GeometryObject = MapUtils.convertUtm33ToWgs84(locationalPosUtm33);

            let lineClasses = classNames({
                "locational": true,
                "locational-line": true,
                "selected": isSelected,
                "confident": roadObject?.locational?.confidence > dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD,
                "uncertain": roadObject?.locational?.confidence <= dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD,
            });

            let circleClasses = classNames({
                "locational": true,
                "locational-circle": true,
                "selected": isSelected,
                "confident": roadObject?.locational?.confidence > dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD,
                "uncertain": roadObject?.locational?.confidence <= dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD,
            })

            if (geometry.type === "Point") {
                locationalLayers.push(
                    <Polyline key="line-1" className={lineClasses} positions={[MapUtils.geoJsonToLatLng(geometry), MapUtils.geoJsonToLatLng(locationalPosition)]} />
                );
                locationalLayers.push(
                    <Circle key="circle-1" className={circleClasses} center={MapUtils.geoJsonToLatLng(locationalPosition)} radius={2} onclick={() => this.props.roadObjectClicked(roadObject.id)} />
                );
            } else {
                if (roadObject.locational?.projectedFromWkt) {
                    let projectedFromPosUtm33 = WKTParser.parse(roadObject.locational.projectedFromWkt);
                    let projectedFromPosition = MapUtils.convertUtm33ToWgs84(projectedFromPosUtm33);

                    locationalLayers.push(
                        <Polyline key="line-1" className={lineClasses} positions={[MapUtils.geoJsonToLatLng(projectedFromPosition), MapUtils.geoJsonToLatLng(locationalPosition)]}/>
                    );
                    locationalLayers.push(
                        <Polyline key="line-2" className={lineClasses} positions={[MapUtils.geoJsonToLatLng(projectedFromPosition, true), MapUtils.geoJsonToLatLng(locationalPosition, true)]}/>
                    );
                    locationalLayers.push(
                        <Circle key="circle-1" className={circleClasses} center={MapUtils.geoJsonToLatLng(locationalPosition)} radius={2} onclick={() => this.props.roadObjectClicked(roadObject.id)}/>
                    );
                    locationalLayers.push(
                        <Circle key="circle-2" className={circleClasses} center={MapUtils.geoJsonToLatLng(locationalPosition, true)} radius={2} onclick={() => this.props.roadObjectClicked(roadObject.id)} />
                    );
                }
            }
        }
        return locationalLayers;
    }

    generateOutlineFeature() {
        let {geometry, roadObject} = this.props;
        if (geometry.type === "Point") {
            let positions = MapUtils.geoJsonToLatLng(geometry);
            let pathClasses = classNames({
                "ro-point": true,
                "outline": true,
            });
            return (
                <Circle center={positions} radius={2} className={pathClasses}/>
            )
        } else {
            let pathClasses = classNames({
                "ro-line": true,
                "outline": true,
            });

            return (
                <GeoJSON data={geometry}
                         className={pathClasses}
                />
            )
        }
    }

    generateFeature() {
        let {geometry, roadObject, isSelected} = this.props;
        if (geometry.type === "Point") {
            let positions = MapUtils.geoJsonToLatLng(geometry);
            let pathClasses = classNames({
                "selected": isSelected,
                "ro-point": true,
                "outline": false,
                "confidence": true,
                "confident": roadObject?.locational?.confidence > dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD,
                "uncertain": roadObject?.locational?.confidence <= dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD,
                "missing": !roadObject?.locational,
            });
            return (
                <Circle  center={positions} radius={2} className={pathClasses} onclick={() => this.props.roadObjectClicked(roadObject.id)}/>
            )
        } else {
            let pathClasses = classNames({
                "selected": isSelected,
                "ro-line": true,
                "outline": false,
                "confident": roadObject?.locational?.confidence > dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD,
                "uncertain": roadObject?.locational?.confidence <= dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD,
                "missing": !roadObject?.locational,
            });

            return (
                <GeoJSON data={geometry}
                         onclick={() => this.props.roadObjectClicked()}
                         className={pathClasses}
                />
            )
        }
    }
}
